import { RoadmapTask, RoadmapData } from '../types';

// Helper function to parse date strings and extract absolute month number (year * 12 + month)
const parseDateToAbsoluteMonth = (dateString: string): number => {
  if (!dateString) {
    const now = new Date();
    return now.getFullYear() * 12 + now.getMonth() + 1;
  }
  
  const lowerDate = dateString.toLowerCase().trim();
  
  // Try to parse YYYY-MM-DD format first (most reliable)
  const isoMatch = dateString.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1]);
    const month = parseInt(isoMatch[2]);
    if (month >= 1 && month <= 12) {
      console.log(`Parsed date from "${dateString}": YYYY-MM-DD -> ${year}-${month} (absolute: ${year * 12 + month})`);
      return year * 12 + month;
    }
  }
  
  // Try to parse YYYY-MM format
  const yearMonthMatch = dateString.match(/(\d{4})-(\d{1,2})/);
  if (yearMonthMatch) {
    const year = parseInt(yearMonthMatch[1]);
    const month = parseInt(yearMonthMatch[2]);
    if (month >= 1 && month <= 12) {
      console.log(`Parsed date from "${dateString}": YYYY-MM -> ${year}-${month} (absolute: ${year * 12 + month})`);
      return year * 12 + month;
    }
  }
  
  // Try to parse MM/DD/YYYY or MM-DD-YYYY format
  const dateMatch = dateString.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dateMatch) {
    const month = parseInt(dateMatch[1]);
    const year = parseInt(dateMatch[3]);
    if (month >= 1 && month <= 12) {
      console.log(`Parsed date from "${dateString}": MM/DD/YYYY -> ${year}-${month} (absolute: ${year * 12 + month})`);
      return year * 12 + month;
    }
  }
  
  // Check for month names with year (e.g., "Dec 2026", "December 2026", "Dec - 2026")
  const monthYearMatch = lowerDate.match(/(january|jan|february|feb|march|mar|april|apr|may|june|jun|july|jul|august|aug|september|sep|october|oct|november|nov|december|dec)\s*-?\s*(\d{4})/);
  if (monthYearMatch) {
    const monthMap: { [key: string]: number } = {
      'january': 1, 'jan': 1,
      'february': 2, 'feb': 2,
      'march': 3, 'mar': 3,
      'april': 4, 'apr': 4,
      'may': 5,
      'june': 6, 'jun': 6,
      'july': 7, 'jul': 7,
      'august': 8, 'aug': 8,
      'september': 9, 'sep': 9,
      'october': 10, 'oct': 10,
      'november': 11, 'nov': 11,
      'december': 12, 'dec': 12
    };
    const monthName = monthYearMatch[1];
    const year = parseInt(monthYearMatch[2]);
    const month = monthMap[monthName];
    if (month) {
      console.log(`Parsed date from "${dateString}": ${monthName} ${year} -> ${year}-${month} (absolute: ${year * 12 + month})`);
      return year * 12 + month;
    }
  }
  
  // Check for month names only (assume current year if no year specified)
  for (const [monthName, monthNum] of Object.entries({
    'january': 1, 'jan': 1,
    'february': 2, 'feb': 2,
    'march': 3, 'mar': 3,
    'april': 4, 'apr': 4,
    'may': 5,
    'june': 6, 'jun': 6,
    'july': 7, 'jul': 7,
    'august': 8, 'aug': 8,
    'september': 9, 'sep': 9,
    'october': 10, 'oct': 10,
    'november': 11, 'nov': 11,
    'december': 12, 'dec': 12
  })) {
    if (lowerDate.includes(monthName)) {
      const now = new Date();
      const year = now.getFullYear();
      console.log(`Parsed date from "${dateString}": ${monthName} (no year, using ${year}) -> ${year}-${monthNum} (absolute: ${year * 12 + monthNum})`);
      return year * 12 + monthNum;
    }
  }
  
  // Try to parse just month numbers (1-12) - assume current year
  const monthMatch = dateString.match(/(\d{1,2})/);
  if (monthMatch) {
    const month = parseInt(monthMatch[1]);
    if (month >= 1 && month <= 12) {
      const now = new Date();
      const year = now.getFullYear();
      console.log(`Parsed date from "${dateString}": Month ${month} (no year, using ${year}) -> ${year}-${month} (absolute: ${year * 12 + month})`);
      return year * 12 + month;
    }
  }
  
  // Default to current month if no valid date found
  const now = new Date();
  console.warn(`Could not parse date from "${dateString}", defaulting to current month (absolute: ${now.getFullYear() * 12 + now.getMonth() + 1})`);
  return now.getFullYear() * 12 + now.getMonth() + 1;
};

// Generate a color based on project name or type
const generateProjectColor = (projectName: string, index: number): string => {
  const colors = [
    '#ff6b6b', '#ffa726', '#ffeb3b', '#4caf50', 
    '#2196f3', '#9c27b0', '#795548', '#607d8b',
    '#e91e63', '#00bcd4', '#8bc34a', '#ff9800'
  ];
  
  // Try to use a consistent color based on project name
  let hash = 0;
  for (let i = 0; i < projectName.length; i++) {
    hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % colors.length;
  
  return colors[colorIndex];
};

// Convert project data to roadmap tasks - using the actual API response structure
export const transformProjectsToRoadmapTasks = (projects: any[]): RoadmapTask[] => {
  if (!projects || projects.length === 0) return [];
  
  console.log('Transforming projects to roadmap tasks:', projects);
  
  return projects.map((project, index) => {
    // Parse start and end months as absolute month numbers (year * 12 + month)
    let startMonth = parseDateToAbsoluteMonth(project.start_month || '');
    let endMonth: number | undefined = undefined;
    
    // Only parse end month if it exists
    if (project.end_month) {
      endMonth = parseDateToAbsoluteMonth(project.end_month);
    } else if (project.estimated_completion) {
      endMonth = parseDateToAbsoluteMonth(project.estimated_completion);
    }
    // If no end month, leave it as undefined - it will be handled in the roadmap chart
    
    console.log(`Processing project ${project.name}:`, {
      start_month: project.start_month,
      end_month: project.end_month,
      estimated_completion: project.estimated_completion,
      parsed_start: startMonth,
      parsed_end: endMonth || 'undefined (will extend to last quarter)'
    });
    
    // Ensure start month is before end month (only if end month exists)
    if (endMonth !== undefined && startMonth > endMonth) {
      console.log(`  Swapping months: ${startMonth} <-> ${endMonth}`);
      [startMonth, endMonth] = [endMonth, startMonth];
    }
    
    const startYear = Math.floor((startMonth - 1) / 12);
    const startMonthNum = ((startMonth - 1) % 12) + 1;
    if (endMonth !== undefined) {
      const endYear = Math.floor((endMonth - 1) / 12);
      const endMonthNum = ((endMonth - 1) % 12) + 1;
      console.log(`  Final absolute months: ${startMonth} (${startMonthNum}/${startYear}) - ${endMonth} (${endMonthNum}/${endYear})`);
    } else {
      console.log(`  Final absolute months: ${startMonth} (${startMonthNum}/${startYear}) - undefined (will extend to last quarter)`);
    }
    
    // Generate dependencies based on dependency_project_id
    const dependencies: string[] = [];
    if (project.dependency_project_id) {
      const dependencyProject = projects.find(p => p.project_id === project.dependency_project_id);
      if (dependencyProject) {
        dependencies.push(dependencyProject.project_id);
      }
    }
    
    // Determine status based on project data (you can customize this logic)
    let status: 'not-started' | 'in-progress' | 'completed' | 'delayed' = 'not-started';
    if (project.estimated_completion) {
      const completionDate = new Date(project.estimated_completion);
      const currentDate = new Date();
      if (completionDate < currentDate) {
        status = 'completed';
      } else if (project.start_month) {
        const startDate = new Date(project.start_month);
        if (startDate <= currentDate) {
          status = 'in-progress';
        }
      }
    }
    
    // Determine priority based on project data (you can customize this logic)
    let priority: 'low' | 'medium' | 'high' | 'critical' = 'medium';
    if (project.presidio_controlled === 'Yes') {
      priority = 'high';
    }
    
    const roadmapTask = {
      id: project.project_id,
      name: project.name,
      startMonth,
      endMonth,
      color: generateProjectColor(project.name, index),
      dependencies,
      description: project.overview_and_goals,
      status,
      priority,
      assignee: project.presidio_controlled === 'Yes' ? 'Presidio' : 'Customer',
      estimatedHours: undefined // Add if you have this data
    };
    
    console.log(`  Created roadmap task:`, roadmapTask);
    return roadmapTask;
  });
};

// Create complete roadmap data from projects
export const createRoadmapDataFromProjects = (projects: any[]): RoadmapData => {
  const tasks = transformProjectsToRoadmapTasks(projects ?? []);
  
  return {
    tasks,
    startYear: new Date().getFullYear()
  };
};

// Helper function to get quarter from month
export const getQuarterFromMonth = (month: number): number => {
  if (month >= 1 && month <= 3) return 1;    // Q1: Jan-Mar
  if (month >= 4 && month <= 6) return 2;    // Q2: Apr-Jun
  if (month >= 7 && month <= 9) return 3;    // Q3: Jul-Sep
  if (month >= 10 && month <= 12) return 4;  // Q4: Oct-Dec
  return 1;
};

// Helper function to get month name
export const getMonthName = (month: number): string => {
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  return monthNames[month - 1] || '';
};